import React, { createContext, useState, useEffect, ReactNode } from 'react';

import { getApiBaseUrl } from '../config/runtimeUrls';
import { useAuth } from '../hooks/useAuth';

export enum TenantPlan {
  FREE = 'free',
  BASIC = 'basic',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise',
}

export enum TenantStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  TRIAL = 'trial',
}

export interface TenantThemeSettings {
  primaryColor: string;
  secondaryColor: string;
  logo?: string;
  favicon?: string;
}

export interface TenantFeatureSettings {
  documentManagement: boolean;
  clientPortal: boolean;
  billing: boolean;
  analytics: boolean;
  aiAssistant: boolean;
  multipleOffices: boolean;
}

export interface TenantPasswordPolicy {
  minLength: number;
  requireSpecialChars: boolean;
  requireNumbers: boolean;
  requireUppercase: boolean;
  expiryDays: number;
}

export interface TenantSecuritySettings {
  mfaRequired: boolean;
  passwordPolicy: TenantPasswordPolicy;
  sessionTimeout: number;
}

export interface TenantLocalizationSettings {
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  language: string;
  currency: string;
}

export interface TenantSettings {
  theme: TenantThemeSettings;
  features: TenantFeatureSettings;
  security: TenantSecuritySettings;
  localization: TenantLocalizationSettings;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: TenantPlan;
  status: TenantStatus;
  settings: TenantSettings;
  createdAt: string;
  updatedAt: string;
}

// Define tenant context type
interface TenantContextType {
  currentTenant: Tenant | null;
  isLoading: boolean;
  error: string | null;
  setCurrentTenant: (tenant: Tenant) => void;
  clearCurrentTenant: () => void;
  isTenantFeatureEnabled: (featureName: keyof TenantFeatureSettings) => boolean;
  getTenantSetting: <K extends keyof TenantSettings, S extends keyof TenantSettings[K]>(
    category: K,
    setting: S
  ) => TenantSettings[K][S] | null;
}

// Create tenant context with default values
export const TenantContext = createContext<TenantContextType>({
  currentTenant: null,
  isLoading: true,
  error: null,
  setCurrentTenant: () => {},
  clearCurrentTenant: () => {},
  isTenantFeatureEnabled: () => false,
  getTenantSetting: () => null,
});

// Props for the tenant provider
interface TenantProviderProps {
  children: ReactNode;
}

// Create the tenant provider component
export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();

  // Initialize tenant state on component mount or when auth state changes
  useEffect(() => {
    const initTenant = async (): Promise<void> => {
      try {
        if (!isAuthenticated || !user) {
          clearCurrentTenant();
          setIsLoading(false);
          return;
        }

        // Try to get tenant from localStorage first
        const storedTenant = localStorage.getItem('currentTenant');
        if (storedTenant) {
          setCurrentTenant(JSON.parse(storedTenant));
          setIsLoading(false);
          return;
        }

        // If no tenant in localStorage, resolve via backend user tenant endpoint
        try {
          const response = await fetch(`${getApiBaseUrl()}/users/${user.id}/tenant`, {
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
          if (response.ok) {
            const result = await response.json();
            const tenant = { id: result?.tenant?.id } as unknown as Tenant;
            if (tenant?.id) {
              // Minimal tenant shape to set context id; details can be fetched later
              const tenantRecord: Tenant = {
                id: tenant.id,
                name: tenant.id,
                slug: tenant.id,
                plan: TenantPlan.BASIC,
                status: TenantStatus.ACTIVE,
                settings: {
                  theme: { primaryColor: '#000', secondaryColor: '#666' },
                  features: {
                    documentManagement: true,
                    clientPortal: true,
                    billing: false,
                    analytics: false,
                    aiAssistant: false,
                    multipleOffices: false,
                  },
                  security: {
                    mfaRequired: false,
                    passwordPolicy: {
                      minLength: 8,
                      requireSpecialChars: false,
                      requireNumbers: true,
                      requireUppercase: false,
                      expiryDays: 90,
                    },
                    sessionTimeout: 30,
                  },
                  localization: {
                    timezone: 'UTC',
                    dateFormat: 'YYYY-MM-DD',
                    timeFormat: 'HH:mm',
                    language: 'en-US',
                    currency: 'USD',
                  },
                },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              setCurrentTenant(tenantRecord);
              localStorage.setItem('currentTenant', JSON.stringify(tenantRecord));
            } else {
              clearCurrentTenant();
            }
          } else {
            clearCurrentTenant();
          }
        } catch (e) {
          console.error('Failed to resolve tenant from backend:', e);
          clearCurrentTenant();
        }
      } catch (err) {
        console.error('Tenant initialization error:', err);
        setError('Failed to initialize tenant');
      } finally {
        setIsLoading(false);
      }
    };

    initTenant();
  }, [isAuthenticated, user]);

  // Set current tenant and store in localStorage
  const handleSetCurrentTenant = (tenant: Tenant): void => {
    setCurrentTenant(tenant);
    localStorage.setItem('currentTenant', JSON.stringify(tenant));
  };

  // Clear current tenant from state and localStorage
  const clearCurrentTenant = (): void => {
    setCurrentTenant(null);
    localStorage.removeItem('currentTenant');
  };

  // Check if a specific tenant feature is enabled
  const isTenantFeatureEnabled = (featureName: keyof TenantFeatureSettings): boolean => {
    if (!currentTenant) return false;
    return currentTenant.settings.features[featureName] || false;
  };

  // Get a specific tenant setting with proper type safety
  const getTenantSetting = <K extends keyof TenantSettings, S extends keyof TenantSettings[K]>(
    category: K,
    setting: S
  ): TenantSettings[K][S] | null => {
    if (!currentTenant) return null;
    return currentTenant.settings[category][setting];
  };

  // Provide the tenant context to children components
  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        isLoading,
        error,
        setCurrentTenant: handleSetCurrentTenant,
        clearCurrentTenant,
        isTenantFeatureEnabled,
        getTenantSetting,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
